import { createContext, useCallback, useContext, useMemo } from "react";
import { SessionProvider, useSession } from "./SessionContext";
import { TeamProvider, useTeam } from "./TeamContext";

const AuthContext = createContext(undefined);

function AuthStateBridge({ children }) {
  const sessionState = useSession();
  const teamState = useTeam();

  const value = useMemo(() => ({
    user: sessionState.user,
    session: sessionState.session,
    loading: sessionState.loading,
    userProfile: sessionState.userProfile,
    profileLoading: sessionState.profileLoading,
    profileError: sessionState.profileError,
    retryProfile: sessionState.retryProfile,
    teams: teamState.teams,
    activeTeam: teamState.activeTeam,
    loadingTeams: teamState.loadingTeams,
    // The teams read failed (after retries) and none are on screen: not the
    // same as "no teams", so pages offer retryTeams instead of Team Setup.
    teamsError: teamState.teamsError,
    signUp: sessionState.signUp,
    signIn: sessionState.signIn,
    signOut: sessionState.signOut,
    getUserProfile: sessionState.getUserProfile,
    refreshSession: sessionState.refreshSession,
    switchActiveTeam: teamState.switchActiveTeam,
    refreshTeams: (preferTeamId) => teamState.refreshTeams(sessionState.user?.id, preferTeamId),
    retryTeams: () => teamState.retryTeams(sessionState.user?.id),
  }), [sessionState, teamState]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function AuthProviderInner({ children }) {
  const teamState = useTeam();
  const { clearTeams, fetchUserTeams } = teamState;

  const handleSessionUserChange = useCallback(async (user, options = {}) => {
    if (options.clear || !user?.id) {
      clearTeams();
      return;
    }
    await fetchUserTeams(user.id);
  }, [clearTeams, fetchUserTeams]);

  return (
    <SessionProvider onSessionUserChange={handleSessionUserChange}>
      <AuthStateBridge>{children}</AuthStateBridge>
    </SessionProvider>
  );
}

export function AuthProvider({ children }) {
  return (
    <TeamProvider>
      <AuthProviderInner>{children}</AuthProviderInner>
    </TeamProvider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
